import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  PLATFORM_ID,
  Inject,
  ChangeDetectorRef,
  ViewChild,
  OnDestroy,
  ElementRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import { LocaleService } from '@app/core/i18n';
import {
  Currency,
  Product,
  Size,
  Breadcrumb,
  CatalogPage,
  Price,
  User,
  UserCivility,
  Language,
  CatalogFacetLink,
  Campaign,
  SeoContent,
  ContentsMarketingFilter,
  Deal
} from '@vestiaire/api-sdk-angular';
import { RefinementsService } from '@app/shared/components/catalog/services/refinements.service';
import { CatalogConfig } from '@app/shared/model/interfaces/catalog';
import { SearchService } from '@app/shared/services/search/search.service';
import { SearchConfig, SearchParameters, SearchChange, SearchResultsFacets } from '@app/core/search/common';
import { InstantSearchService } from './services/instantsearch.service';
import {
  OnCatalogPageSearch,
  SetCatalogNbPages,
  SetCatalogNbProducts,
  SetCatalogPageNumber
} from '@app/+catalog/store';
import * as fromUser from '@app/core/user/store/reducers';
import { isPlatformServer, isPlatformBrowser } from '@angular/common';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { ActivatedRoute } from '@angular/router';
import { Router, ActivationEnd, RouterEvent } from '@angular/router';
import { CatalogSortComponent } from '@app/shared/components/catalog/catalog-sort/catalog-sort.component';
import { PlatformLocation } from '@angular/common';
import * as CatalogActions from '@app/+catalog/store/actions';
import { ISearchState } from '@app/shared/model/interfaces/store/search-state';
import { FiltersService } from './services/filters.service';
import { AlgoliaInsightService } from '@app/shared/components/catalog/services/algolia-insight.service';
import * as fromDataLayer from '@app/analytics/actions/data-layer';
import { filter, concatMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { getPageParams } from '@app/core/header/store/reducers';
import { getDirectShippingEligibility } from '@app/core/i18n/store/reducers';
import { CatalogLinksResolverResponse } from '@app/+catalog/catalog-links-resolver.service';
import { environment } from '@env/environment';
import { update } from 'lodash';

export interface ProductHit extends Product {
  priceEur?: Price;
}

export interface TrackingProductList {
  products: ProductHit[];
  brandFacet?: Object;
  page: CatalogPage;
}

export interface TrackingProduct {
  positions: number[];
  objectIDs: string[];
}

@Component({
  selector: 'vc-catalog',
  templateUrl: './catalog.component.html',
  styleUrls: ['./catalog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  /**
   * The config object to pass to the instantsearch instance.
   */
  instantSearchConfig: SearchConfig;

  @Input()
  page: CatalogPage | null;
  @Input()
  config: CatalogConfig;
  @Input()
  searchParameters: SearchParameters = {};
  @Input()
  breadcrumb: Array<Breadcrumb> | null = [];
  @Input()
  likedItem: string[];
  @Input()
  facetLink: CatalogFacetLink | null;
  @Input()
  isFirstPage: boolean;
  @Input()
  currentCountry: string;
  @Input()
  pageType: string;
  @Input()
  private searchStore: ISearchState;
  @Input()
  campaign: Campaign;
  @Input()
  seoLinks: CatalogLinksResolverResponse | null;
  @Input()
  seoContents: SeoContent | null;
  @Input()
  marketingFilter: ContentsMarketingFilter[];

  private translationKey: string;
  private locatedInCustomTag = false;
  private customTag = '';
  private preventReset: boolean;
  private trackingInfos: { hitsPerPage: number; page: number; indexName: string };
  private indexName: string;
  private indexName$: Subject<string>;
  private ngUnsubscribe = new Subject<void>();

  public userToken: string;

  public indexCalculator: number;

  public filterMenuIsOpened_onMobile = false;

  applyPriceFilter: Event;
  public currentPageNumber = 0;
  public totalPageNumber = 0;
  public isUserDSeligible: boolean;
  public usersCountry: string;
  public isLocalCountry: boolean;
  public isSustainableLeafEnable = environment.feature.sustainable_leaf || false;
  public hasSellerRating = environment.feature.seller_rating;
  public marketingFilterLocal: ContentsMarketingFilter;
  public currentDeal: Deal;

  searchState: SearchParameters;
  searchQuery: string;
  queryID?: string;

  isLogged = false;
  productsLiked: number[];
  isMobile = false;
  sortListOpen = false;

  nbHits: number;
  facets: {};
  refinedFacets: string[];

  isServer = false;
  showPagination = true;
  catalogLinksAttribute = 'catalogLinksWithoutLanguage';
  trackByHit = (index: number, item) => index;

  catalogContainerOffset: number;

  triggerRefinedCountries: MouseEvent | null = null;

  @ViewChild('catalogPage__catalog', { static: false }) catalogContainer: ElementRef;
  @ViewChild(CatalogSortComponent, { static: false })
  customSort: CatalogSortComponent;

  constructor(
    private store: Store<IAppState>,
    private localeService: LocaleService,
    private platformService: PlatformService,
    private searchService: SearchService,
    private refinementsService: RefinementsService,
    private instantSearchService: InstantSearchService,
    private activatedRoute: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private location: PlatformLocation,
    private algoliaSearch: AlgoliaInsightService,
    private filtersService: FiltersService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.indexName$ = new Subject();
    this.indexName$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({ next: value => (this.indexName = value) });

    this.store
      .select(getPageParams)
      .pipe(
        filter(d => d && d.pageType === 'catalog'),
        concatMap(value => this.indexName$),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: data => {
          this.store.dispatch(new fromDataLayer.Persist({ index_name: data }));
        }
      });

    this.isServer = isPlatformServer(this.platformId);
    this.translationKey = localeService.getTranslationKey();
    this.usersCountry = localeService.getCountry();
    if (this.platformService.isBrowser) {
      this.store
        .select('device')
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({ next: res => (this.isMobile = !res.isMdUp) });
    }
    this.store
      .pipe(select(getDirectShippingEligibility), takeUntil(this.ngUnsubscribe))
      .subscribe({ next: res => (this.isUserDSeligible = res) });

    this.activatedRoute.data.subscribe({
      next: data => {
        this.store.dispatch(
          new fromDataLayer.Persist({
            onsite_campaign_id: data.campaign ? data.campaign.id : ''
          })
        );
      }
    });
    this.store.pipe(select('catalog')).subscribe(
      res => {
        this.currentDeal = res?.deal || null;
      },
      error => {
        console.error(error);
      }
    );
  }

  ngOnInit() {
    if (!this.config) {
      return;
    }

    this.location.onPopState(() => {
      this.preventReset = true;
    });

    if (this.config.hitsPerPage) {
      this.searchParameters.hitsPerPage = this.config.hitsPerPage;
    }

    if (!this.config.indexName) {
      this.refinementsService.getSortByValue().subscribe(
        sortBy => (this.config.indexName = sortBy.name),
        error => null
      );
    }

    if (this.config.facetFilter) {
      this.searchParameters.facetFilters = this.config.facetFilter;
    }
    if (this.config.numericFilters) {
      this.searchParameters.numericFilters = this.config.numericFilters;
    }

    this.instantSearchConfig = this.instantSearchService.createConfig(
      this.config.indexName,
      this.config.searchFunction,
      this.searchParameters,
      this.config.withFilters || this.config.withPagination
    );
    // Enable personnalisation of the Algolia hits depending on the userToken
    this.searchParameters.enablePersonalization = environment.envName === 'production';

    this.algoliaSearch.getUserToken().subscribe(
      token => {
        this.searchParameters.userToken = token;
      },
      error => {
        console.error(error);
      }
    );

    if (this.config.withFilters) {
      this.config.currentRefinementOnly = this.instantSearchService.getCurrentRefinementOnly();
    }

    // Retrieve tag from resolved params
    this.activatedRoute.url.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      _ => {
        this.locatedInCustomTag = false;
        this.customTag = '';
        this.refinementsService.isStockPage = false;

        const { pageData } = this.activatedRoute.snapshot.data;

        if (pageData && pageData.route) {
          pageData.route.parameters.forEach(routeParam => {
            if (routeParam.name === 'stock' || routeParam.name === 'tag') {
              this.refinementsService.isStockPage = routeParam.name === 'stock';
              this.locatedInCustomTag = true;
              this.customTag = routeParam.value;
            }
          });
        }
      },
      err => {}
    );
    this.initPaginationVisibility();
    this.router.events
      // .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        res => this.callResetOnCustomSort(res as RouterEvent),
        _ => {}
      );
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(PLATFORM_ID)) {
      this.catalogContainerOffset = this.catalogContainer.nativeElement.getBoundingClientRect().top;
      this.store.pipe(select(fromUser.isAuthenticated), takeUntil(this.ngUnsubscribe)).subscribe(
        res => {
          this.isLogged = res;
        },
        err => {}
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.marketingFilter && changes.marketingFilter.currentValue) {
      this.updateMarketingFilter(changes.marketingFilter.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  callResetOnCustomSort(navEvent: RouterEvent) {
    /**
     * Check if the requested uri is not productPage
     * If it isn't then call the reset of the sorting in the child sort
     */
    if (navEvent instanceof ActivationEnd) {
      const { data } = navEvent.snapshot;
      const isProductPage = data && data.routename === 'product';
      if (!isProductPage && !this.preventReset && this.customSort) {
        this.customSort.resetSelected();
      }

      this.preventReset = false;
    }
  }

  // Check if editorPicks returned is matches with algolia data name
  isCustomAlgoliaData(algoliaDataName: string): boolean {
    return this.refinementsService.editorPicksIds
      .map(eP => eP.algoliaDataAttr.toLowerCase())
      .includes(algoliaDataName.toLowerCase());
  }

  // Check if the key of algolia return is part of the custom extract tags
  isPartOfExtractTags(name: string, extractTags): boolean {
    let matched = false;
    for (const key in extractTags) {
      if (extractTags[`${key}`] === name) {
        matched = true;
      }
    }
    return matched;
  }

  onSearchChange({ results, state }: SearchChange) {
    if (!results && !state) {
      return;
    }
    /**
     * Sometimes, algolia return `results.query`
     * is empty (when it match a brand or a known category), so we
     * retrieve the query directly from the store
     */
    const { searchStore } = this;
    const query = searchStore && searchStore.searchValue ? searchStore.searchValue : '';

    // Set currentPageNumber and totalPageNumber in order to
    // toggle the showMore BTN
    this.currentPageNumber = results.page;
    this.totalPageNumber = results.nbPages;

    this.refinementsService.clearRefinements();
    const { page, nbPages, nbHits } = results;
    this.store.dispatch(new SetCatalogPageNumber({ pageNumber: page + 1 }));
    this.store.dispatch(new SetCatalogNbPages({ nbPages }));
    this.store.dispatch(new SetCatalogNbProducts({ nbProducts: nbHits }));

    const datalayerKeys = {
      keyword: query || 'no keyword'
    };
    if (results._rawResults && results._rawResults[0]?.abTestVariantID) {
      datalayerKeys['abTestVariantID'] = results._rawResults[0].abTestVariantID;
    }
    this.store.dispatch(new fromDataLayer.Persist(datalayerKeys));

    if ((this.platformService.isServer && nbHits === 0) || !results) {
      return;
    }

    this.searchState = state;
    this.searchQuery = query;

    this.queryID = results.queryID;

    this.registerLastSearch(query, results.nbHits);

    if (results['_rawResults']) {
      const result = results['_rawResults'][0];
      this.facets = result.facets;
      this.nbHits = result.nbHits;

      // Grab Branding + Universe + CatLvl1 + CatLvl2
      // for injection into  tags
      const extractTags = {
        modelFacetName: `model.facet`,
        brandFacetName: `brand.facet`,
        universeFacetName: `universe.facet.${this.translationKey}`,
        catLvl1FacetName: `category.facet.${this.translationKey}.lvl0`,
        catLvl2FacetName: `category.facet.${this.translationKey}.lvl1`,
        editorFacetName: `editorPicks.facet.${this.translationKey}`
      };
      let oneBrand = false;

      const currentCats = {};
      results.disjunctiveFacets.forEach((facet: SearchResultsFacets) => {
        if (this.isPartOfExtractTags(facet.name, extractTags)) {
          /** Parse custom tags for vintage / we love / fast delivery */
          if (this.locatedInCustomTag) {
            // case 2 :
            // Check if vintage || Welove .. and inject
            this.refinementsService.editorPicksIds.forEach(editorPick => {
              if (editorPick.resolvedName === this.customTag) {
                this.refinementsService.setEditorRefinements({
                  label: this.page.content.headline,
                  staticField: true
                });
              }
              return;
            });

            // Make sure we exit the loop if more than one data returned by algolia, meaning
            // we are retrieving all root values ( aka we're not filtering )
            if (Object.keys(facet.data).length > 1) {
              return;
            }
          }

          /** Parse results for universe / cat / cat2 */
          if (Object.keys(facet.data).length === 1 && !this.isCustomAlgoliaData(Object.keys(facet.data)[0])) {
            if (facet.name === 'brand.facet') {
              oneBrand = true;
            }
            if (facet.name === 'model.facet' && !oneBrand) {
              return;
            }
            // Make sure we do not inject custom data tags
            if (!this.isCustomAlgoliaData(Object.keys(facet.data)[0])) {
              currentCats[`${facet.name}`] = [...Object.keys(facet.data), 'staticField'];
              return;
            }
          }
        }
      });

      // Push search values into refinements service
      // in order to display in alert popup + cat header
      this.refinementsService.setRefinements({
        ...currentCats,
        ...state['disjunctiveFacetsRefinements'],
        ...state['hierarchicalFacetsRefinements'],
        ...state['numericRefinements']
      });

      this.refinedFacets = [
        ...Object.keys(state['disjunctiveFacetsRefinements']),
        ...Object.keys(state['hierarchicalFacetsRefinements']),
        ...Object.keys(state['numericRefinements'])
      ];
      this.trackingInfos = {
        hitsPerPage: result.hitsPerPage,
        page: result.page,
        indexName: result.index
      };

      this.indexCalculator = result.page * result.hitsPerPage;

      // Tracking list_product
      const trackingHits = result['hits'].slice(0, Math.min(3, result['hits'].length)) || [];

      const productList: ProductHit[] = [];
      trackingHits.map(hit => {
        const productHit: ProductHit = this.productFromAlgoliaHit(hit);
        productHit.priceEur = {
          currency: Currency.EUR,
          cents: hit.price.cents[Currency.EUR],
          formatted: this.localeService.formatCurrency(hit.price.cents[Currency.EUR] / 100)
        };
        productList.push(productHit);
      });

      if (this.page) {
        const trackingProductList = {
          products: productList,
          brandFacet: this.facets['brand.facet'],
          page: this.page
        } as TrackingProductList;

        this.store.dispatch(new OnCatalogPageSearch(trackingProductList));
      }

      if (this.indexName !== result.indexUsed) {
        this.indexName$.next(result.indexUsed);
      }
    }
  }

  productFromAlgoliaHit(hit): Product {
    // Must match attributesToRetrieve parameter

    const hitTags = [];
    if (hit.editorPicks) {
      if (hit.editorPicks.vintage) {
        hitTags.push('vintage');
      }
      if (hit.editorPicks.welove) {
        hitTags.push('welove');
      }
    }

    const currentCurrency = this.localeService.getCurrency();
    const currentLanguage = this.localeService.getTranslationKey();

    const civility: UserCivility = {
      name: 'mr',
      localizedName: 'mr',
      idGender: 1
    };

    const language: Language = {
      name: 'en',
      localizedName: 'en',
      code: 'en'
    };

    const seller: User = {
      id: '0',
      type: 'user',
      firstname: hit.firstname,
      civility: civility,
      language: language,
      sellerRating: {
        badge: this.hasSellerRating ? hit.seller?.badge?.[currentLanguage] : hit.seller.badge
      },
      isOfficialStore: hit.seller?.isOfficialStore,
      hyperwalletActive: false, // not used on catalog
      alreadyDepositedAProduct: false // not used on catalog
    };

    const product: Product = {
      id: hit.id,
      type: 'product',
      name: hit.name.searchable[currentLanguage],
      price: {
        currency: currentCurrency as Currency,
        cents: hit.price.cents[currentCurrency],
        formatted: this.localeService.formatCurrency(hit.price.cents[currentCurrency] / 100)
      },
      brand: { id: hit.brand.id, type: 'brand', localizedName: hit.brand.searchable[0], name: hit.brand.searchable[0] },
      pictures: [{ path: hit.pictures[0] }],
      path: hit.link[this.localeService.getTranslationKey()],
      tags: hitTags,
      sold: hit.sold.sold,
      inStock: hit.stock,
      likeCount: hit.likes.count,
      shouldBeGone: hit.shouldBeGone,
      isoCountry: hit.country,
      seller: seller,
      flags: [],
      dsEligibleCountries: hit.dsEligibleCountries
    };
    if (
      this.isUserDSeligible &&
      hit.dsEligibleCountries &&
      hit.dsEligibleCountries.length > 0 &&
      hit.dsEligibleCountries.includes(this.usersCountry)
    ) {
      product.flags.push('direct-shipping');
    }

    if (this.isSustainableLeafEnable && hit.localCountries && hit.localCountries.includes(this.usersCountry)) {
      product.flags.push('local-country');
    }

    if (hit.editorPicks && hit.editorPicks?.dealEligible) {
      product.flags.push('deal-eligible');
    }

    if (hit.discount) {
      product.regularPrice = {
        currency: currentCurrency as Currency,
        cents: hit.discount.originalPriceCents[currentCurrency],
        formatted: this.localeService.formatCurrency(hit.discount.originalPriceCents[currentCurrency] / 100)
      };
    }
    if (hit.size) {
      const size: Size = {
        id: hit.size.id,
        type: 'size',
        size: hit.size.label
      };
      product.size = size;
    }
    return product;
  }

  next(elem, selector) {
    let nextEl: HTMLElement;
    let currentSibling = elem;
    while (nextEl === undefined) {
      currentSibling = currentSibling.nextSibling;
      if (currentSibling && currentSibling.className) {
        nextEl = currentSibling;
        return nextEl;
      } else if (!currentSibling) {
        return null;
      }
    }
    return null;
  }

  bindFilterAccordion() {
    const filterTitles = document.body.querySelectorAll('.ais-RefinementList-header');
    for (let i = 0; i < filterTitles.length; i++) {
      filterTitles[i].addEventListener(
        'click',
        event => {
          const elemTitle = (<HTMLElement>event.target).parentElement;
          const elemToSlide = this.next(elemTitle.parentNode, '.ais-RefinementList-body');
          const closedTitleClass = 'ais-RefinementList-header--closed';

          if (elemTitle.classList.contains(closedTitleClass)) {
            elemTitle.classList.remove(closedTitleClass);
            elemToSlide.style.display = 'block';
          } else {
            elemTitle.classList.add(closedTitleClass);
            elemToSlide.style.display = 'none';
          }
        },
        { passive: true }
      );
    }
  }

  collapseFilters() {
    const filterTitles = document.body.querySelectorAll('.ais-RefinementList-header');
    for (let i = 0; i < filterTitles.length; i++) {
      const elemTitle = filterTitles[i];
      const elemToSlide = this.next(elemTitle.parentNode, '.ais-RefinementList-body');
      const closedTitleClass = 'ais-RefinementList-header--closed';

      elemTitle.classList.add(closedTitleClass);
      elemToSlide.style.display = 'none';
    }
  }

  toggleFilterMenu(event, isApplyButton) {
    this.filterMenuIsOpened_onMobile = !this.filterMenuIsOpened_onMobile;
    if (isApplyButton) {
      this.applyPriceFilterOnMobile(event);
    }
  }

  closeFilterMenu() {
    this.filterMenuIsOpened_onMobile = false;
  }

  actionBarClicked(event) {
    if (event === 'sort') {
      this.sortListOpen = !this.sortListOpen;
    }

    if (event === 'filters') {
      this.toggleFilterMenu(event, false);
    }
  }

  applyPriceFilterOnMobile(event) {
    this.applyPriceFilter = event;
    this.changeDetectorRef.markForCheck();
    this.filtersService.forcePriceFilterUpdate();
  }

  initPaginationVisibility() {
    // FIXME : Get route name from store when obfuscation MR is done
    /** On SSR mode, allow pagination only for following route names
     * categorie-marque-couleur
     * categorie-marque-matiere
     * souscategorie-marque
     * souscategorie-marque-modele
     * souscategorie-marque-couleur
     * souscategorie-marque-matiere
     */
    if (this.isMobile && !this.isServer) {
      this.showPagination = false;
    }
  }

  trackClickedFilter(event) {
    // Algolia tracking (we track only click on sizes and gender ATM)
    this.algoliaSearch.clickFilter(this.indexName, event);

    // Will trigger a call to TagCommander & our tracking
    this.store.dispatch({
      type: CatalogActions.APPLY_FILTER,
      payload: { filterItem: event.filterItem, filterOptions: event.filterOptions }
    });
  }

  trackSeoLinksClick(event) {
    this.store.dispatch({
      type: CatalogActions.TRACK_SEO_LINK_CLICK,
      payload: { label: event.label, position: event.position + 1, url: event.path }
    });
  }

  trackProductClick(event: TrackingProduct) {
    // Split tracking in two part:
    // - first part is for click on a product on a catalog on the home/landing page (CMS tracking)
    // - second part is default tracking on the catalog

    const basePayload = {
      // Algolia positions starts at 1, but we want tracking positions to start at 0, so we add -1...
      position: (event.positions[0] - 1 + this.trackingInfos.page * this.trackingInfos.hitsPerPage).toString(),
      indexName: this.indexName,
      productId: event.objectIDs[0]
    };

    this.algoliaSearch.clickAfterSearch(this.queryID, this.indexName, event.positions, event.objectIDs);

    if (this.config && this.config.tracking && (this.pageType === 'home' || this.pageType === 'landing')) {
      const payload = {
        blockSubCategory: this.config.tracking,
        page_type: this.pageType,
        ...basePayload
      };
      this.store.dispatch({ type: CatalogActions.TRACK_PRODUCT_FEED_CLICK, payload });
    } else {
      const payload = {
        keyword: this.searchQuery === '' ? 'no keyword' : this.searchQuery,
        ...basePayload
      };
      this.store.dispatch({ type: CatalogActions.TRACK_PRODUCT_CLICK, payload });
    }
  }

  registerLastSearch(query, nbHits) {
    // Register into lastsearches...
    if (this.page && this.page.content.headline === this.searchStore.searchValue) {
      // ...brand's title if we are in brand page and the last search is a brand suggestion...
      this.searchService.registerSearch({
        query: this.searchStore.searchValue,
        nbHits: nbHits
      });
    } else if (query && query !== '' && nbHits > 0) {
      // ...or the query if we are on a single search page
      this.searchService.registerSearch({
        query: query,
        nbHits: nbHits
      });
    }
  }

  getProductFlags(hit): string[] {
    const r = [];
    if (hit.dsEligibleCountries && hit.dsEligibleCountries.includes(this.usersCountry)) {
      r.push('direct_shipping');
    }
    return r;
  }

  refinedCountries(event) {
    this.triggerRefinedCountries = event;
  }

  updateMarketingFilter(mf) {
    this.marketingFilterLocal = mf.filter(obj => obj.filterType === 'sustainability')[0];
  }
}
